import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Box, InternalGrid, Text } from '@paygreen/paygreen-ui';
import { timestampToDateWithHour, IsMobile } from '../../../../utils/tools';
import {
    StatusDisplayTable,
    CarbonDataDisplay,
    LineKeyValue,
} from '../../../../components';
import { generateCO2DataOrigins } from '../../dashboard/co2OriginsTools';
import { statusOptions } from '../statusOptions';
import { offsetsDetailsList } from './offsetsDetailsList';

const OffsetsDetailsDisplay = ({ data, children, t }) => {
    const dataCO2Origins =
        data && generateCO2DataOrigins(data.emissionDistribution, t);
    const improvedTextSize = IsMobile() ? 'sm' : 'xs';
    const hasPurchasedDate = [
        'PURCHASED',
        'CONTRIBUTED',
        'OFFSET_FROM_ANOTHER_VENDOR',
    ].includes(data?.status);

    return (
        <>
            <Box marginBottom="lg" marginTop="xs">
                <InternalGrid
                    displayType="grid"
                    gridTemplateColumns={IsMobile() ? '1fr' : 'auto auto'}
                    justifyItems="start"
                    alignItems="baseline"
                    gridGap="sm"
                    hasStaticWidth={false}
                >
                    {offsetsDetailsList.map((item, index) => (
                        <LineKeyValue
                            key={index}
                            hasUppercase={false}
                            keyLabel={t('offsets.details.' + item.name)}
                            keyValue={
                                item.type === 'date'
                                    ? timestampToDateWithHour(data?.[item.name])
                                    : item.type === 'completedDate' &&
                                      hasPurchasedDate
                                    ? timestampToDateWithHour(
                                          data?.purchasedAt || data?.updatedAt,
                                      )
                                    : data?.[item.name]
                            }
                            textSize={improvedTextSize}
                            paddingLeft={IsMobile() ? 'none' : 'md'}
                            defaultValue={t('offsets.details.notCompleted')}
                        >
                            {item.type === 'status' ? (
                                <StatusDisplayTable
                                    status={data?.status}
                                    statusOptions={statusOptions}
                                    i18TextBase="offsets.status"
                                    textSize={improvedTextSize}
                                />
                            ) : item.type === 'dataCarbon' ? (
                                <CarbonDataDisplay
                                    value={data?.[item.name]}
                                    mode="carbon"
                                    textSize={improvedTextSize}
                                    isInTable={false}
                                />
                            ) : item.type === 'dataPrice' ? (
                                <Text textSize="xxs">
                                    <Text htmlTag="span">
                                        <CarbonDataDisplay
                                            value={data?.[item.name]}
                                            mode="price"
                                            textSize={improvedTextSize}
                                        />
                                    </Text>

                                    {data?.[item.name] !== '0' && (
                                        <Text htmlTag="em" textSize="xs">
                                            {t('offsets.details.projectPrice')}
                                        </Text>
                                    )}
                                </Text>
                            ) : null}
                        </LineKeyValue>
                    ))}
                </InternalGrid>
            </Box>

            <InternalGrid>
                <Text htmlTag="strong" colorWab="grey40" marginBottom="xs">
                    {t('offsets.details.subtitleOrigins')}
                </Text>
            </InternalGrid>

            <InternalGrid
                displayType="grid"
                gridTemplateColumns={IsMobile() ? '1fr' : 'auto auto'}
                justifyItems="start"
                alignItems="center"
                gridGap="sm"
                hasStaticWidth={false}
            >
                {dataCO2Origins &&
                    dataCO2Origins.map((card, index) => (
                        <LineKeyValue
                            key={index}
                            hasUppercase={false}
                            keyLabel={card.type}
                            keyValue={card.value || '0'}
                            textSize={improvedTextSize}
                            paddingLeft={IsMobile() ? 'none' : 'md'}
                        >
                            <CarbonDataDisplay
                                value={card.value || '0'}
                                mode="price"
                                isInTable={false}
                                hasConversionMode={false}
                                unit="%"
                                textSize={improvedTextSize}
                            />
                        </LineKeyValue>
                    ))}
            </InternalGrid>

            {children}
        </>
    );
};

OffsetsDetailsDisplay.propTypes = {
    data: PropTypes.object,
};

export default withTranslation('common')(OffsetsDetailsDisplay);
